/**
 * Ahrefs Web Analytics, loaded only after the visitor allows analytics.
 *
 * Until 2026-10-21 this was a plain <head> tag (scripts/thirdPartyHead.ts) that
 * loaded on every page before the consent banner was answered, and the banner
 * had no effect on it. It now follows the same answer as Google Analytics:
 *
 * - The script is injected only when `effectiveConsent()` is `granted`. Decline,
 *   no answer yet (in `strict` consent mode), or Do Not Track / Global Privacy
 *   Control (reported as `denied`) means it never loads.
 * - It is loaded with `data-no-pageview-auto`. Without that flag analytics.js
 *   wraps `history.pushState` and reports every in-app navigation by itself, so
 *   once loaded it could not be stopped. With it, the script sends nothing on
 *   its own, and every page view goes through `sendAhrefsPageView`, which
 *   re-checks consent each time. Withdrawing consent (Decline after Allow, from
 *   the footer "Cookie choices" control) therefore stops tracking from the next
 *   page on, without a reload.
 * - Production hostname only, the same guard as GA (`isProductionHost`), so dev,
 *   preview and CI never report to the live Ahrefs project.
 *
 * What it sends, read from analytics.js itself on 2026-10-21: the event name,
 * page URL, page title, referrer, browser language, screen width, height and
 * pixel ratio, and the site key. No cookie was observed. It also skips sending
 * when `navigator.webdriver` is set, so automated browsers never report.
 */
import { consentApplies, effectiveConsent } from '@/lib/ads'
import { isProductionHost } from '@/lib/ga'

export const AHREFS_SRC = 'https://analytics.ahrefs.com/analytics.js'
const AHREFS_KEY = 'WucpDBxXYRQ4I+TdEp0W0g'

type AhrefsWindow = Window & { AhrefsAnalytics?: { sendEvent: (name: string) => void } }

/*
 * No separate "is Ahrefs configured" switch: a deployment with neither ads nor
 * GA never asks the consent question (`consentApplies()` is false), so this
 * never loads there. `effectiveConsent()` rather than the stored answer, so
 * `regional` consent mode (src/config/consent.ts) opts in an unanswered
 * visitor outside the consent regions, as it does for Google's tags.
 */
const allowed = (): boolean =>
  typeof window !== 'undefined' &&
  isProductionHost(window.location.hostname) &&
  consentApplies() &&
  effectiveConsent() === 'granted'

let loading = false

/**
 * Reports one page view for the current URL, loading the script first if this
 * is the first allowed page view. A no-op without consent.
 */
export const sendAhrefsPageView = (): void => {
  if (!allowed()) return
  const w = window as AhrefsWindow
  if (w.AhrefsAnalytics) {
    w.AhrefsAnalytics.sendEvent('pageview')
    return
  }
  if (loading) return // the onload below reports the page the visitor is on by then
  loading = true
  const script = document.createElement('script')
  script.async = true
  script.src = AHREFS_SRC
  script.setAttribute('data-key', AHREFS_KEY)
  script.setAttribute('data-no-pageview-auto', '')
  script.dataset.ahrefs = 'true'
  script.onload = () => {
    if (allowed()) (window as AhrefsWindow).AhrefsAnalytics?.sendEvent('pageview')
  }
  script.onerror = () => {
    // Blocked by an extension or offline: allow a later page view to retry.
    loading = false
    script.remove()
  }
  document.head.appendChild(script)
}

/** Test-only: forget that a load was started. */
export const resetAhrefsForTests = (): void => {
  loading = false
}
